import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import Col from 'react-bootstrap/Col';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import {
    GET_ALL_DRIVERS_BY_REGION,
    GET_FIELD_OP_BOXES,
    GET_CHASIS_DATA,
    GET_LOAD_ACCESSORIAL,
} from '../../../graphql/client/queries';
import { VALIDATE_SBX_BOX } from '../../../graphql/client/mutations';
import { LoadAccessorialEnum } from '../../../types/LoadAccessorialEnum';
import { LoadStatusEnum } from '../../../types/LoadStatusEnum';
import { PermissionEnum } from '../../../types/PermissionEnum';
import {
    GetAllChasisData,
    GetAllChasisData_getAllChasisData as ChasisDetails,
    GetAllDrivers,
    GetAllDrivers_getAllDrivers as DriverData,
    GetFieldOpBoxes,
    GetFieldOpBoxes_getFieldOpBoxes_boxDetails as FieldOpBoxDetails,
    GetLoadAccessorial,
    GetLoadAccessorial_getLoadAccessorial as LoadAccessorialData,
    GetLoadDetail_getLoadDetail as LoadDetail,
} from '../../../types/schemaTypes';
import { useAuthContext } from '../../Auth/AuthProvider';
import { Option } from '../../Users/interface';
import { CustomButton } from '../../_common';
import SearchableDropdown from '../../_common/SearchableDropdown/SearchableDropdown';
import { Textfield } from '../../_common/Textfield/Textfield';
import './EditLoadDetail.scss';
import useEditLoadDetail from './useEditLoadDetail';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { THREE_NUMBERS_MASK, SPECIAL_CHARACTERS_MASK } from '../../../utils/helpers/maskHelper';

library.add(fas);

interface EditLoadDetailProps {
    onClickShow: () => void;
    onClickHide: () => void;
    show: boolean;
    loadId: number;
    loadDetail: LoadDetail;
    driverStatus: Option[];
}

interface Suggestion {
    id: string | number;
    name: string;
}

const CONCURRENT_EDITS_ERROR_MESSAGE = 'Load no longer eligible for driver assignment, click OK to refresh.';

const EditLoadDetail = ({ onClickHide, show, loadId, loadDetail, driverStatus }: EditLoadDetailProps): React.ReactElement => {
    const [hasConcurrentError, setHasConcurrentError] = useState(false);
    const onUpdateError = (): void => setHasConcurrentError(true);
    const canAddDriver = useAuthContext().userHasPermission(PermissionEnum.ManageLoads);
    const { data: driversData, refetch } = useQuery<GetAllDrivers>(GET_ALL_DRIVERS_BY_REGION, {
        variables: { regionId: loadDetail.regionId },
    });
    const { data: boxesData } = useQuery<GetFieldOpBoxes>(GET_FIELD_OP_BOXES, {
        variables: {
            jobId: loadDetail.loadJobId,
        },
        fetchPolicy: 'network-only',
    });
    const { data: chasisData } = useQuery<GetAllChasisData>(GET_CHASIS_DATA, {
        variables: {
            jobId: loadDetail.loadJobId,
        },
        fetchPolicy: 'network-only',
    });

    const [validateSBXBox] = useMutation(VALIDATE_SBX_BOX);

    const driversName: Suggestion[] = [];
    const boxes: Suggestion[] = [];
    const chassis: Suggestion[] = [];

    const checkForPrimaryChassisNumber = (): string => {
        if (loadDetail.chassisId != 0) {
            return loadDetail.chassisNumber;
        } else return '';
    };

    const checkForSecondaryChassisNumber = (): string => {

        if (loadDetail.isSplit) {
            if (loadDetail.secondaryChassisId != 0 ) {
                return loadDetail.secondaryChassisNumber ? loadDetail.secondaryChassisNumber : '';
            } else return '';
        } else return '';
    };

    if (boxesData?.getFieldOpBoxes?.boxDetails) {
        (boxesData.getFieldOpBoxes.boxDetails as (FieldOpBoxDetails | null)[]).forEach((element: FieldOpBoxDetails | null): void => {
            if (element != null) {
                boxes.push({
                    id: element.boxId ?? 0,
                    name: element.boxDesc ?? '',
                });
            }
        });
    }

    if (chasisData?.getAllChasisData) {
        (chasisData.getAllChasisData as (ChasisDetails | null)[]).forEach((element: ChasisDetails | null): void => {
            if (element != null) {
                chassis.push({
                    id: element.id ?? 0,
                    name: element.sbsId ?? '',
                });
            }
        });
    }

    if (driversData?.getAllDrivers) {
        (driversData.getAllDrivers as (DriverData | null)[]).forEach((element: DriverData | null): void => {
            if (element != null && element.driverName != null) {
                driversName.push({
                    id: element.driverId,
                    name: element.driverName,
                });
            }
        });
    }
    const { data: loadAccessorialData } = useQuery<GetLoadAccessorial>(GET_LOAD_ACCESSORIAL, {
        variables: {
            input: {
                loadId: loadId,
                accessorialName: [LoadAccessorialEnum.DEAD_HEAD, LoadAccessorialEnum.CHAINUP_TOWUP],
            },
        },
    });
    let loadAccessorials: (LoadAccessorialData | null)[] | null;
    if (loadAccessorialData && loadAccessorialData.getLoadAccessorial) {
        loadAccessorials = loadAccessorialData.getLoadAccessorial;
    }
    const checkForMiles = (): string => {
        const loadAccessorial =
            loadAccessorials &&
            loadAccessorials
                .filter(it => it && it.accessorialName === LoadAccessorialEnum.DEAD_HEAD)
                .map(it => it && it.value)[0];
        if (loadAccessorial !== null && loadAccessorial !== undefined) {
            return loadAccessorial;
        }
        return '';
    };
    const checkForChainup = (): string => {
        const loadAccessorial =
            loadAccessorials &&
            loadAccessorials.filter(it => it && it.accessorialId === 2).map(it => it && it.checked)[0];
        if (loadAccessorial !== null && loadAccessorial !== undefined) {
            return loadAccessorial;
        }
        return '';
    };
    const checkForDeadHead = (): string => {
        const loadAccessorial =
            loadAccessorials &&
            loadAccessorials.filter(it => it && it.accessorialId === 1).map(it => it && it.checked)[0];
        if (loadAccessorial !== null && loadAccessorial !== undefined) {
            return loadAccessorial;
        }
        return '';
    };

    const checkForDisableDriverName = (): boolean => {
        if (loadDetail.status === LoadStatusEnum.CREATED && canAddDriver) {
            return false;
        }
        return true;
    };

    const checkForDisableDriverStatus = (): boolean => {
        if (
            (loadDetail.status === LoadStatusEnum.CREATED || loadDetail.status === LoadStatusEnum.ASSIGNED) &&
            canAddDriver
        ) {
            return false;
        }
        return true;
    };

    const checkForDisableForSecondaryDriver = (): boolean => {
        if (loadDetail.status === LoadStatusEnum.DELIVERED || loadDetail.status === LoadStatusEnum.COMPLETED) {
            return true;
        } else if (canAddDriver && loadDetail.secondaryDriverName === '') {
            return false;
        }
        return true;
    };

    const checkForDisableForSecondaryDriverStatus = (): boolean => {
        if (loadDetail.status === LoadStatusEnum.DELIVERED || loadDetail.status === LoadStatusEnum.COMPLETED) {
            return true;
        } else if (canAddDriver) {
            return false;
        }
        return true;
    };

    const {
        handleSubmit,
        handleDropdownChange,
        handleInputChange,
        handleAutoCompleteChange,
        inputs,
        handleCheckBox,
        handleCancel,
        errorMessage,
        handleOnClickOK,
        milesError,
        boxError,
        secondaryBoxError,
        chassisError,
        secondaryChassisError,
        truckNumberError,
        secondaryTruckNumberError,
        tmsIdError,
    } = useEditLoadDetail(
        loadDetail,
        loadId,
        onClickHide,
        checkForMiles(),
        checkForChainup(),
        checkForDeadHead(),
        onUpdateError,
        refetch,
        validateSBXBox,
    );

    const checkForErrors = (): boolean => {
        if (
            milesError ||
            boxError ||
            secondaryBoxError ||
            chassisError ||
            secondaryChassisError ||
            truckNumberError ||
            secondaryTruckNumberError ||
            tmsIdError ||
            errorMessage !== ''
        ) {
            return true;
        }
        return false;
    };

    const checkIfCustomerIsHalliburton = (): boolean => {
        if (loadDetail.customerId === 46 || loadDetail.customerId === 6 || loadDetail.customerId === 85) {
            return true;
        }
        return false;
    };
    const searchIcon = <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'search' }} />;

    return (
        <Modal show={show} onHide={onClickHide} size="lg">
            <Modal.Header className="EditLoad-EditHeader">
                <div className="EditLoad-HeaderLeft">
                    <i className="material-icons EditLoad-EditIcon">create</i>
                    <span className="EditLoad-EditLink">Edit</span>
                    <span className="EditLoad-TitleText-Detail">Load Details</span>
                    <span className="EditLoad-TitleTextError">
                        {hasConcurrentError && errorMessage === '' ? (
                            <span>
                                An edit has already been made for this load. Refresh the load profile to try again.
                            </span>
                        ) : (
                            ''
                        )}
                    </span>
                </div>
                {errorMessage !== '' && <div className="EditLoad-DriverItem-Assign-Error">{errorMessage}</div>}
                {errorMessage === CONCURRENT_EDITS_ERROR_MESSAGE ? (
                    <div className="EditLoad-HeaderRight">
                        <div className="EditLoad-ButtonWrapper">
                            <CustomButton isBordered={true} title="OK" onClick={handleOnClickOK} />
                        </div>
                    </div>
                ) : (
                    <div className="EditLoad-HeaderRight">
                        {hasConcurrentError && (
                            <div className="EditLoad-ButtonWrapper">
                                <CustomButton
                                    title="Refresh"
                                    isBordered
                                    onClick={() => window.location.reload()}
                                >
                                    <i className="material-icons">refresh</i>
                                </CustomButton>
                            </div>
                        )}
                        <div className="EditLoad-ButtonWrapper">
                            <CustomButton
                                isBordered={true}
                                title="Cancel"
                                onClick={() => {
                                    handleCancel();
                                }}
                            />
                        </div>
                        <div className="EditLoad-ButtonWrapper">
                            <CustomButton
                                isBordered={false}
                                title="Save"
                                onClick={handleSubmit}
                                isDisabled={checkForErrors()}
                            />
                        </div>
                    </div>
                )}
            </Modal.Header>
            <Modal.Body>
                <div className="EditLoad-Border">
                    <div className="InitialModal-Body">
                        <div className="EditLoad-Left">
                            <div
                                className={
                                    loadDetail.isSplit
                                        ? 'EditLoad-BodyLeftDetail EditLoad-BodyLeftWithBottom'
                                        : 'EditLoad-BodyLeftDetail'
                                }
                            >
                                <Row>
                                    <span className="EditLoad-PrimaryLabelText">Accessorials</span>
                                </Row>
                                <Row>
                                    <Col className="Check-Col">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="gridCheck1"
                                            defaultChecked={
                                                checkForChainup() !== '' && checkForChainup() !== 'N' ? true : false
                                            }
                                            onChange={() => handleCheckBox(inputs.chainUpCheckStatus, 'chainUp')}
                                        />
                                        <label className="form-check-label">Chainup/ Tow Up</label>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="Check-Col">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="gridCheck2"
                                            defaultChecked={
                                                checkForDeadHead() !== '' && checkForDeadHead() !== 'N' ? true : false
                                            }
                                            onChange={() => handleCheckBox(inputs.deadHeadCheckStatus, 'deadHead')}
                                        />
                                        <label className="form-check-label">Dead Head</label>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={8} className="EditLoad-Miles-Container">
                                        <Textfield
                                            label="MILES"
                                            name="miles"
                                            value={checkForMiles()}
                                            onChange={event => {
                                                handleInputChange(event);
                                            }}
                                            disabled={inputs.deadHeadCheckStatus !== 'Y'}
                                            error={milesError !== ''}
                                            helperText={milesError}
                                            mask={THREE_NUMBERS_MASK}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </div>
                        <div className="EditLoad-Right">
                            <div className="EditLoad-BodyRight">
                                <Row>
                                    <span className="EditLoad-PrimaryLabelText">Operational Details</span>
                                </Row>
                                <Row className="EditLoad-OperationalDetails">
                                    <Col md={3}>
                                        <Textfield
                                            label="TRANSPORTATION PO"
                                            name="transportationPo"
                                            value={loadDetail.transportationPo || ''}
                                            onChange={event => {
                                                handleInputChange(event);
                                            }}
                                            mask={SPECIAL_CHARACTERS_MASK}
                                        />
                                    </Col>
                                    {checkIfCustomerIsHalliburton() && (
                                        <Col md={3}>
                                            <Textfield
                                                label="TMS ID"
                                                name="tmsId"
                                                value={loadDetail.tmsId || ''}
                                                onChange={event => {
                                                    handleInputChange(event);
                                                }}
                                                error={tmsIdError !== ''}
                                                helperText={tmsIdError}
                                            />
                                        </Col>
                                    )}
                                </Row>
                                <Row>
                                    <span className="EditLoad-SecondaryLabelText">Primary Driver</span>
                                </Row>
                                <Row className="EditLoad-PrimaryDetails">
                                    <Col md={3} className="EditLoad-DriverSearch-Container">
                                        <SearchableDropdown
                                            id="driverName"
                                            label="DRIVER"
                                            autoCompleteEnabled={true}
                                            dropdownOptions={driversName}
                                            value={inputs.driverName}
                                            onSelect={(eventKey: string | null) => {
                                                handleAutoCompleteChange('driverName', {
                                                    id: eventKey,
                                                    name: inputs.driverName,
                                                });
                                            }}
                                            disabled={checkForDisableDriverName()}
                                            endIcon={searchIcon}
                                        />
                                    </Col>
                                    <Col md={2} className="EditLoad-DriverLoadStatus-Container">
                                        <SearchableDropdown
                                            disabled={checkForDisableDriverStatus()}
                                            dropdownOptions={driverStatus}
                                            id="driverLoadStatus"
                                            label="DRIVER STATUS"
                                            onSelect={(eventKey: string | null) => {
                                                handleDropdownChange('driverLoadStatus', eventKey);
                                            }}
                                            value={
                                                loadDetail.driverId !== loadDetail.secondaryDriverId
                                                    ? inputs.driverLoadStatus
                                                    : loadDetail.driverLoadStatus
                                            }
                                        />
                                    </Col>
                                    {<Col md={2} className={loadDetail.isBellyDump ? 'EditLoad-ChassisSearch-Container-disabled' : 'EditLoad-ChassisSearch-Container'}>
                                        <SearchableDropdown
                                            id="boxNumber"
                                            label="BOX #"
                                            autoCompleteEnabled={true}
                                            dropdownOptions={boxes}
                                            value={loadDetail.isBellyDump? 'N/A' : inputs.boxNumber}
                                            onSelect={(eventKey: string | null) => {
                                                handleAutoCompleteChange('boxNumber', {
                                                    id: eventKey,
                                                    name: inputs.boxNumber,
                                                });
                                            }}
                                            disabled={loadDetail.isSplit || loadDetail.isBellyDump ? true : false}
                                            hasError={boxError !== ''}
                                            errorMessage={boxError}
                                            endIcon={searchIcon}
                                        />
                                    </Col>}
                                    {<Col md={2} className={loadDetail.isBellyDump ? 'EditLoad-ChassisSearch-Container-disabled' : 'EditLoad-ChassisSearch-Container'}>
                                        <SearchableDropdown
                                            id="chassisNumber"
                                            label="CHASSIS #"
                                            autoCompleteEnabled={true}
                                            dropdownOptions={chassis}
                                            value={loadDetail.isBellyDump? 'N/A' :checkForPrimaryChassisNumber()}
                                            onSelect={(eventKey: string | null) => {
                                                handleAutoCompleteChange('chassisNumber', {
                                                    id: eventKey,
                                                    name: inputs.chassisNumber,
                                                });
                                            }}
                                            hasError={chassisError !== ''}
                                            errorMessage={chassisError}
                                            endIcon={searchIcon}
                                            disabled={loadDetail.isBellyDump ? loadDetail.isBellyDump : false}
                                        />
                                    </Col>}
                                    <Col md={2}>
                                        <Textfield
                                            label="TRUCK #"
                                            name="truckNumber"
                                            value={loadDetail.truckNumber || ''}
                                            onChange={event => {
                                                handleInputChange(event);
                                            }}
                                            error={truckNumberError !== ''}
                                            helperText={truckNumberError}
                                        />
                                    </Col>
                                </Row>
                                {loadDetail.isSplit ? (
                                    <Row>
                                        <span className="EditLoad-SecondaryLabelText">Secondary Driver</span>
                                    </Row>
                                ) : (
                                    ''
                                )}
                                {loadDetail.isSplit ? (
                                    <Row>
                                        <Col md={3} className="EditLoad-DriverSearch-Container">
                                            <SearchableDropdown
                                                id="secondaryDriverName"
                                                label="SECONDARY DRIVER"
                                                autoCompleteEnabled={true}
                                                dropdownOptions={driversName}
                                                value={inputs.secondaryDriverName}
                                                onSelect={(eventKey: string | null) => {
                                                    handleAutoCompleteChange('secondaryDriverName', {
                                                        id: eventKey,
                                                        name: inputs.secondaryDriverName,
                                                    });
                                                }}
                                                disabled={checkForDisableForSecondaryDriver()}
                                                endIcon={searchIcon}
                                            />
                                        </Col>
                                        <Col md={2} className="EditLoad-DriverLoadStatus-Container">
                                            <SearchableDropdown
                                                disabled={checkForDisableForSecondaryDriverStatus()}
                                                dropdownOptions={driverStatus}
                                                id="secondaryDriverLoadStatus"
                                                label="DRIVER STATUS"
                                                onSelect={(eventKey: string | null) => {
                                                    handleDropdownChange('secondaryDriverLoadStatus', eventKey);
                                                }}
                                                value={inputs.secondaryDriverLoadStatus}
                                            />
                                        </Col>
                                        <Col md={2} className="EditLoad-ChassisSearch-Container">
                                            <SearchableDropdown
                                                id="secondaryBoxNumber"
                                                label="BOX #"
                                                autoCompleteEnabled={true}
                                                dropdownOptions={boxes}
                                                value={inputs.boxNumber}
                                                onSelect={(eventKey: string | null) => {
                                                    handleAutoCompleteChange('secondaryBoxNumber', {
                                                        id: eventKey,
                                                        name: inputs.secondaryBoxNumber,
                                                    });
                                                }}
                                                disabled={loadDetail.isSplit ? loadDetail.isSplit : false}
                                                hasError={secondaryBoxError !== ''}
                                                errorMessage={secondaryBoxError}
                                                endIcon={searchIcon}
                                            />
                                        </Col>
                                        <Col md={2} className="EditLoad-ChassisSearch-Container">
                                            <SearchableDropdown
                                                id="secondaryChassisNumber"
                                                label="CHASSIS #"
                                                autoCompleteEnabled={true}
                                                dropdownOptions={chassis}
                                                extraProp={chasisData}
                                                value={checkForSecondaryChassisNumber()}
                                                onSelect={(eventKey: string | null) => {
                                                    handleAutoCompleteChange('secondaryChassisNumber', {
                                                        id: eventKey,
                                                        name: inputs.secondaryChassisNumber,
                                                    });
                                                }}
                                                hasError={secondaryChassisError !== ''}
                                                errorMessage={secondaryChassisError}
                                                endIcon={searchIcon}
                                            />
                                        </Col>
                                        <Col md={2}>
                                            <Textfield
                                                label="TRUCK #"
                                                name="secondaryTruckNumber"
                                                value={loadDetail.secondaryTruckNumber || ''}
                                                onChange={event => {
                                                    handleInputChange(event);
                                                }}
                                                error={secondaryTruckNumberError !== ''}
                                                helperText={secondaryTruckNumberError}
                                            />
                                        </Col>
                                    </Row>
                                ) : (
                                    ''
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="InitialModal-Footer"></Modal.Footer>
        </Modal>
    );
};

export default EditLoadDetail;

